import {
	IModel,
	db,
	IEventable,
	IIdentifiable,
	IEventInfo,
	enumOrDefault,
	VIPProject,
	IVIPProject,
	IVIPProjectOptions,
	VIPCertificateIp,
	IVIPCertificateIp,
	IVIPCertificateIpOptions,
	VIPCertificateEvent,
	IVIPCertificateEvent,
	IVIPCertificateEventOptions,
} from "@/loader";
import dayjs, { Dayjs } from "dayjs";

export enum VipCertificateStatus {
	draft = "draft",
	checking = "checking",
	validating = "validating",
	sending = "sending",
	awaiting = "awaiting",
	signed = "signed",
	abandonned = "trash",
}

export function vipCertificateStatusOrDefault(value: any, defaultValue: VipCertificateStatus = VipCertificateStatus.draft): VipCertificateStatus {
	return <VipCertificateStatus>enumOrDefault(value, defaultValue, Object.values(VipCertificateStatus));
}

export function getVipCertificateStatusLabel(value: VipCertificateStatus): string {
	switch (value) {
		case VipCertificateStatus.draft:
			return window.wp.i18n._x("Draft", "Certificate", "vip-vue");
		case VipCertificateStatus.checking:
			return window.wp.i18n._x("Checking", "Certificate", "vip-vue");
		case VipCertificateStatus.validating:
			return window.wp.i18n._x("Validating", "Certificate", "vip-vue");
		case VipCertificateStatus.sending:
			return window.wp.i18n._x("Sending", "Certificate", "vip-vue");
		case VipCertificateStatus.awaiting:
			return window.wp.i18n._x("Awaiting", "Certificate", "vip-vue");
		case VipCertificateStatus.signed:
			return window.wp.i18n._x("Signed", "Certificate", "vip-vue");
		case VipCertificateStatus.abandonned:
			return window.wp.i18n._x("Abandonned", "Certificate", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Status", "Certificate", "vip-vue");
	}
}

export function getVipCertificateStatusList(): { value: any, text: string }[] {
	return [
		{ value: "draft", text: window.wp.i18n._x("Draft", "Certificate", "vip-vue") },
		{ value: "checking", text: window.wp.i18n._x("Checking", "Certificate", "vip-vue") },
		{ value: "validating", text: window.wp.i18n._x("Validating", "Certificate", "vip-vue") },
		{ value: "sending", text: window.wp.i18n._x("Sending", "Certificate", "vip-vue") },
		{ value: "awaiting", text: window.wp.i18n._x("Awaiting", "Certificate", "vip-vue") },
		{ value: "signed", text: window.wp.i18n._x("Signed", "Certificate", "vip-vue") },
		{ value: "trash", text: window.wp.i18n._x("Abandonned", "Certificate", "vip-vue") },
	];
}

export enum VipCertificateType {
	abilityCertificate = "ability",
	workCertificate = "work",
}

export function vipCertificateTypeOrDefault(value: any, defaultValue: VipCertificateType = VipCertificateType.abilityCertificate): VipCertificateType {
	return <VipCertificateType>enumOrDefault(value, defaultValue, Object.values(VipCertificateType));
}

export function getVipCertificateTypeLabel(value: VipCertificateType): string {
	switch (value) {
		case VipCertificateType.abilityCertificate:
			return window.wp.i18n._x("Ability Certificate", "Certificate", "vip-vue");
		case VipCertificateType.workCertificate:
			return window.wp.i18n._x("Work Certificate", "Certificate", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Type", "Certificate", "vip-vue");
	}
}

export function getVipCertificateTypeList(): { value: any, text: string }[] {
	return [
		{ value: "ability", text: window.wp.i18n._x("Ability Certificate", "Certificate", "vip-vue") },
		{ value: "work", text: window.wp.i18n._x("Work Certificate", "Certificate", "vip-vue") },
	];
}

export type VIPCertificateFields = "id" | "fk_project" | "date" | "type" | "status" | "data";

/**
 * Certificate Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPCertificateBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** Project ID */
	fk_project?: number;
	/** Date */
	date?: Dayjs | null;
	/** Status */
	status?: VipCertificateStatus;
	/** Type */
	type?: VipCertificateType;
	/** Data */
	data?: string | null;
	/** Event information used to create an event on various actions. */
	event?: IEventInfo | null;

	/** Project */
	project?: IVIPProject | null;

	/** Certificate ips */
	ips?: IVIPCertificateIp[];
	/** Certificate events */
	events?: IVIPCertificateEvent[];
}

/**
 * Certificate Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPCertificateOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the Project ID should be included or not. Default true. */
	fk_project?: boolean;
	/** Indicates whether the Date should be included or not. Default true. */
	date?: boolean;
	/** Indicates whether the Status should be included or not. Default true. */
	status?: boolean;
	/** Indicates whether the Type should be included or not. Default true. */
	type?: boolean;
	/** Indicates whether the Data should be included or not. Default true. */
	data?: boolean;
	/** Indicates whether the Project should be included or not. Default false. */
	project?: boolean | IVIPProjectOptions;
	/** Indicates whether the Certificate ips should be included or not. Default false. */
	ips?: boolean | IVIPCertificateIpOptions;
	/** Indicates whether the Certificate events should be included or not. Default false. */
	events?: boolean | IVIPCertificateEventOptions;
}

/**
 * Certificate Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPCertificateBase implements IVIPCertificateBase, IModel<IVIPCertificateBase>, IEventable {
	/** Id */
	public id!: number;
	/** Project ID */
	public fk_project!: number;
	/** Date */
	public date!: Dayjs | null;
	/** Status */
	public status!: VipCertificateStatus;
	/** Type */
	public type!: VipCertificateType;
	/** Data */
	public data!: string | null;
	/** Event information used to create an event on various actions. */
	public event: IEventInfo | null = null;

	/**
	 * Get Project
	 */
	public get project(): VIPProject | null {
		return db.projects.get(this.fk_project);
	}

	/**
	 * Set Project
	 */
	public set project(value: VIPProject | null) {
		this.fk_project = Number.orDefault(value?.id);
	}

	/**
	 * Get Certificate ips
	 */
	public get ips(): VIPCertificateIp[] {
		return db.certificateIPS.list.filter(x => x.fk_certificate === this.id);
	}

	/**
	 * Get Certificate events
	 */
	public get events(): VIPCertificateEvent[] {
		return db.certificateEvents.list.filter(x => x.fk_certificate === this.id);
	}

	/**
	 * Check if this Status is Draft.
	 *
	 * @return bool
	 */
	public get isDraft(): boolean {
		return this.status === VipCertificateStatus.draft;
	}

	/**
	 * Check if this Status is Checking.
	 *
	 * @return bool
	 */
	public get isChecking(): boolean {
		return this.status === VipCertificateStatus.checking;
	}

	/**
	 * Check if this Status is Validating.
	 *
	 * @return bool
	 */
	public get isValidating(): boolean {
		return this.status === VipCertificateStatus.validating;
	}

	/**
	 * Check if this Status is Sending.
	 *
	 * @return bool
	 */
	public get isSending(): boolean {
		return this.status === VipCertificateStatus.sending;
	}

	/**
	 * Check if this Status is Awaiting.
	 *
	 * @return bool
	 */
	public get isAwaiting(): boolean {
		return this.status === VipCertificateStatus.awaiting;
	}

	/**
	 * Check if this Status is Signed.
	 *
	 * @return bool
	 */
	public get isSigned(): boolean {
		return this.status === VipCertificateStatus.signed;
	}

	/**
	 * Check if this Status is Abandonned.
	 *
	 * @return bool
	 */
	public get isAbandonned(): boolean {
		return this.status === VipCertificateStatus.abandonned;
	}

	/**
	 * Check if this Type is Ability Certificate.
	 *
	 * @return bool
	 */
	public get isTypeAbilityCertificate(): boolean {
		return this.type === VipCertificateType.abilityCertificate;
	}

	/**
	 * Check if this Type is Work Certificate.
	 *
	 * @return bool
	 */
	public get isTypeWorkCertificate(): boolean {
		return this.type === VipCertificateType.workCertificate;
	}

	/**
	 * Get the Status label
	 *
	 * @return string
	 */
	public getStatusLabel(): string {
		return getVipCertificateStatusLabel(this.status);
	}

	/**
	 * Get the Type label
	 *
	 * @return string
	 */
	public getTypeLabel(): string {
		return getVipCertificateTypeLabel(this.type);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPCertificateBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPCertificateBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPCertificateBase) {
		this.id = Number.orDefault(data.id);
		this.fk_project = Number.orDefault(data.fk_project);
		this.date = dayjs.nullable(data.date);
		this.status = vipCertificateStatusOrDefault(data.status, VipCertificateStatus.draft);
		this.type = vipCertificateTypeOrDefault(data.type);
		this.data = String.nullable(data.data);
		
		if (data.project) {
			db.projects.insertOrUpdate(data.project);
		}
		
		if (Array.isArray(data.ips) && data.ips.length) {
			db.certificateIPS.insertOrUpdateMany(data.ips);
		}
		if (Array.isArray(data.events) && data.events.length) {
			db.certificateEvents.insertOrUpdateMany(data.events);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPCertificateBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPCertificateBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_project !== undefined) {
			this.fk_project = Number.orDefault(data.fk_project);
		}
		if (data.date !== undefined) {
			this.date = dayjs.nullable(data.date);
		}
		if (data.status !== undefined) {
			this.status = vipCertificateStatusOrDefault(data.status, VipCertificateStatus.draft);
		}
		if (data.type !== undefined) {
			this.type = vipCertificateTypeOrDefault(data.type);
		}
		if (data.data !== undefined) {
			this.data = String.nullable(data.data);
		}
		
		if (data.project) {
			db.projects.insertOrUpdate(data.project);
		}
		
		if (Array.isArray(data.ips) && data.ips.length) {
			db.certificateIPS.insertOrUpdateMany(data.ips);
		}
		if (Array.isArray(data.events) && data.events.length) {
			db.certificateEvents.insertOrUpdateMany(data.events);
		}
	}
}
